// Contas da agenda que a semana e o mês usam: cor de cada parceira, expediente de cada uma,
// horários livres (sem almoço, folgas e bloqueios) e ocupação do dia.
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.ts';
import type { AgendaItem, Professional, Settings } from './data.ts';
import { addDays, dowIndex, isoLocal, nowLocal, toMin } from './format.ts';

export const DOW_KEY = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'] as const;
// mesma paleta do servidor (catalog.ts), para quando a cor não veio
export const AGENDA_COLORS = ['#D97980', '#8C7A70', '#6E8F86', '#B08A5A', '#7C6A9A', '#5F7E9B'];
export const proColor = (p: Pick<Professional, 'color'> | undefined, i: number) => p?.color || AGENDA_COLORS[i % AGENDA_COLORS.length]!;

export const isLive = (a: AgendaItem) => a.status !== 'cancelled' && a.status !== 'no_show';
// a semana da agenda começa no domingo
export const weekStart = (d: string) => addDays(d, -dowIndex(d));
export const endMin = (a: AgendaItem) => { const e = isoLocal(a.endsAt); return e.date > a.date ? 24 * 60 : e.min; };

export interface Block { id: string; professionalId: string | null; startsAt: string; endsAt: string; reason: string }
export const useBlocks = () => useQuery({ queryKey: ['blocks'], queryFn: () => api.get<Block[]>('/blocks'), staleTime: 60_000 });

// expediente do dia: o da parceira (se ela tem horário próprio) ou o do salão
export function hoursOf(day: string, s: Settings, pro?: Pick<Professional, 'workHours'>): { on: boolean; ini: number; fim: number } {
  const key = DOW_KEY[dowIndex(day)]!;
  const own = pro?.workHours?.[key];
  const h = own ?? s.hours[key];
  return { on: !!h?.on && !!s.hours[key]?.on, ini: toMin(h?.ini ?? '09:00'), fim: toMin(h?.fim ?? '19:00') };
}

// o que ocupa a parceira no dia: horários, almoço, folgas e bloqueios (em minutos do dia)
function busyOf(day: string, pro: Professional, items: AgendaItem[], s: Settings, blocks: Block[]): Array<[number, number]> {
  const out: Array<[number, number]> = items.filter((a) => a.date === day && a.professionalId === pro.id && isLive(a)).map((a) => [toMin(a.time), endMin(a)]);
  if (s.lunch.on) out.push([toMin(s.lunch.ini), toMin(s.lunch.fim)]);
  for (const b of blocksOn(day, blocks, pro.id)) out.push([b.s, b.e]);
  return out.sort((x, y) => x[0] - y[0]);
}

export function blocksOn(day: string, blocks: Block[], proId: string): Array<{ id: string; s: number; e: number; reason: string }> {
  return blocks.filter((b) => b.professionalId === null || b.professionalId === proId).flatMap((b) => {
    const s = isoLocal(b.startsAt), e = isoLocal(b.endsAt);
    if (s.date > day || e.date < day) return [];
    return [{ id: b.id, s: s.date < day ? 0 : s.min, e: e.date > day ? 24 * 60 : e.min, reason: b.reason }];
  }).filter((x) => x.e > x.s);
}

// intervalos livres da parceira no dia; no passado não há horário livre
export function freeOf(day: string, pro: Professional, items: AgendaItem[], s: Settings, blocks: Block[]): Array<[number, number]> {
  const h = hoursOf(day, s, pro);
  if (!h.on) return [];
  const now = nowLocal();
  if (day < now.date) return [];
  let cur = day === now.date ? Math.max(h.ini, Math.ceil(now.min / 15) * 15) : h.ini;
  const out: Array<[number, number]> = [];
  for (const [bs, be] of busyOf(day, pro, items, s, blocks)) {
    if (be <= cur) continue;
    if (bs > cur) out.push([cur, Math.min(bs, h.fim)]);
    cur = Math.max(cur, be);
    if (cur >= h.fim) break;
  }
  if (cur < h.fim) out.push([cur, h.fim]);
  return out.filter(([a, b]) => b - a >= 15);
}

// ocupação do dia: minutos marcados ÷ minutos de expediente das parceiras (sem almoço e bloqueios)
export function dayStats(day: string, pros: Professional[], items: AgendaItem[], s: Settings, blocks: Block[]) {
  const live = items.filter((a) => a.date === day && isLive(a) && pros.some((p) => p.id === a.professionalId));
  let cap = 0, busy = 0;
  for (const p of pros) {
    const h = hoursOf(day, s, p);
    if (!h.on) continue;
    let open = h.fim - h.ini;
    if (s.lunch.on) open -= Math.max(0, Math.min(h.fim, toMin(s.lunch.fim)) - Math.max(h.ini, toMin(s.lunch.ini)));
    for (const b of blocksOn(day, blocks, p.id)) open -= Math.max(0, Math.min(h.fim, b.e) - Math.max(h.ini, b.s));
    cap += Math.max(0, open);
    busy += live.filter((a) => a.professionalId === p.id).reduce((t, a) => t + Math.max(0, Math.min(endMin(a), h.fim) - Math.max(toMin(a.time), h.ini)), 0);
  }
  const open = pros.some((p) => hoursOf(day, s, p).on);
  return { count: live.length, open, pct: cap ? Math.min(100, Math.round((busy / cap) * 100)) : 0 };
}

export const heat = (pct: number, count: number) => (!count ? 'h0' : pct < 35 ? 'h1' : pct < 65 ? 'h2' : pct < 90 ? 'h3' : 'full');
export const durShort = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, '0') : ''}` : `${m} min`);
